/**
 * <chat-cute> dresses the Shopify Inbox chat bubble to match the My Rewards button:
 * a round light pink stitched button with a dark pink chat icon and little treats floating
 * inside, smaller on phones and gently floating. The bubble lives inside the chat's open
 * shadow roots, where page CSS can't reach, so this adds a style tag inside each of them.
 */
const STYLE_ID = 'ccc-chat-cute';

// Tiny heart, sparkles and a sprinkle around the edge of a round button (52 x 52).
const TREATS = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 52 52'%3E%3Cg transform='translate(9 9) scale(.2)'%3E%3Cpath d='M24 42S6 31 6 18a9 9 0 0 1 18-3 9 9 0 0 1 18 3c0 13-18 24-18 24z' fill='%23D91F6B'/%3E%3C/g%3E%3Cg transform='translate(34 8) scale(.34)'%3E%3Cpath d='M12 0C13 8 16 11 24 12C16 13 13 16 12 24C11 16 8 13 0 12C8 11 11 8 12 0Z' fill='%23F7A93E'/%3E%3C/g%3E%3Crect x='35' y='38' width='7' height='2.6' rx='1.3' transform='rotate(35 38.5 39.3)' fill='%23D91F6B'/%3E%3Cg transform='translate(10 36) scale(.25)'%3E%3Cpath d='M12 0C13 8 16 11 24 12C16 13 13 16 12 24C11 16 8 13 0 12C8 11 11 8 12 0Z' fill='%23E7739F'/%3E%3C/g%3E%3C/svg%3E")`;

const CHAT_CSS = `
.activator {
  --activator-bg: #FFF4EE !important;
  --activator-fg: #B0124F !important;
  box-sizing: border-box !important;
  position: relative !important;
  overflow: hidden !important;
  width: 52px !important;
  min-width: 52px !important;
  height: 52px !important;
  padding: 0 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  background: #FFF4EE !important;
  color: #B0124F !important;
  border: 2px solid #B0124F !important;
  border-radius: 999px !important;
  outline: 2px dashed #E7739F;
  outline-offset: -6px;
  box-shadow: 0 10px 22px -8px rgb(176 18 79 / 0.45) !important;
  cursor: pointer;
}
.activator .activator__label { display: none !important; }
.activator .activator__icon {
  position: relative;
  z-index: 1;
  width: 22px !important;
  height: 22px !important;
  color: #B0124F !important;
  opacity: 1 !important;
}
.activator .activator__icon svg { stroke: #B0124F !important; stroke-width: 2.2; }
.activator::before {
  content: "";
  position: absolute;
  inset: 0;
  background: ${TREATS} center / 100% 100% no-repeat;
  pointer-events: none;
}
.activator:hover { filter: brightness(1.07); }

/* Chat window (incl. Track my order) in the pinky look; colours come from the theme's chat variables. */
.panel {
  background-color: #FFF4EE !important;
  border: 4px solid #F2C9D3 !important;
  box-shadow: 0 0 0 3px #fff, 0 26px 60px -20px rgb(217 31 107 / 0.55) !important;
}
h1, h2, h3, h4 {
  font-family: var(--ccc-heading, Georgia, serif) !important;
  color: #D91F6B !important;
  letter-spacing: -0.01em;
}
input, textarea, select { border-radius: 16px !important; }
input:focus, textarea:focus { outline-color: #D91F6B !important; }
button[type="submit"] { border-radius: 999px !important; font-weight: 700 !important; }

/* Sign in button (normally Shop purple) matches the My Rewards pill. */
.sign-in-button,
.sign-in-button.sign-in-button--shop {
  background-color: #FBD3E0 !important;
  color: #B0124F !important;
  border: 2px solid #B0124F !important;
  font-family: var(--ccc-heading, Georgia, serif) !important;
  font-weight: 800 !important;
}
.sign-in-button:hover,
.sign-in-button.sign-in-button--shop:hover:not(:active) { background-color: #F9C2D6 !important; opacity: 1 !important; }
.sign-in-button:focus-visible { outline-color: #B0124F !important; }

@media (max-width: 47.999rem) {
  .activator { width: 42px !important; min-width: 42px !important; height: 42px !important; outline-offset: -5px; }
  .activator .activator__icon { width: 18px !important; height: 18px !important; }
}

@media (prefers-reduced-motion: no-preference) {
  @keyframes ccc-chat-float {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-10px); }
  }
  @keyframes ccc-treats-twinkle {
    0%, 100% { transform: translateY(1px) rotate(0deg); opacity: 1; }
    50% { transform: translateY(-2px) rotate(8deg); opacity: 0.7; }
  }
  .activator::before { animation: ccc-treats-twinkle 2.8s ease-in-out -1.4s infinite; }
}
`;

class ChatCute extends HTMLElement {
  connectedCallback() {
    this.timer = setInterval(() => this.dress(), 800);
    this.dress();
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  dress() {
    document.querySelectorAll('shopify-chat').forEach((chat) => {
      if (chat.shadowRoot) this.visit(chat.shadowRoot);
    });
  }

  visit(root) {
    if (!root.getElementById(STYLE_ID)) {
      const style = document.createElement('style');
      style.id = STYLE_ID;
      const heading = getComputedStyle(document.body).getPropertyValue('--font-heading').trim() || 'Georgia, serif';
      style.textContent = `:host { --ccc-heading: ${heading}; }\n${CHAT_CSS}`;
      root.appendChild(style);
    }
    root.querySelectorAll('*').forEach((element) => {
      if (element.shadowRoot) this.visit(element.shadowRoot);
    });
  }
}

if (!customElements.get('chat-cute')) customElements.define('chat-cute', ChatCute);
